import type { MySample } from '@tabel/shared/schemas/agent-data';
import { useQuery } from '@tanstack/react-query';
import { Inbox, ShieldAlert } from 'lucide-react';
import { type ReactNode, useEffect, useId } from 'react';
import { mySamplesKey, requestMySamples } from '../../components/DayCardAgentLink';
import { Card, EmptyState, Skeleton } from '../../components/ui';
import { ApiError, reportClientError } from '../../lib/errors';
import { ruDateTime } from '../../lib/format';
import { useRole } from '../../lib/role';
import { describeCondition, useConditionNames } from '../AdminAgentData/condition';
import { say as sampleSay } from '../AdminAgentData/texts';
import { Fact, LoadError, SectionHeading, shouldReport } from './parts';
import { LABEL, T, TITLE } from './texts';

/*
 * «Мои выборки» на странице «Мои ИИ-клиенты» (005, T140; spec.md FR-026; contracts/http-api.md, `GET /api/me/samples`):
 * выборки дней, которые получил агент пользователя, и «Передать день агенту»: номер, время, клиент, условие, число
 * дней. Ответ — `MySamplesResponse` (`@tabel/shared/schemas/agent-data`), запрос и ключ кэша — общие с «Передать день
 * агенту» (`components/DayCardAgentLink.tsx`); сервер присылает новые сверху. Условие словами — `AdminAgentData/
 * condition.ts`: правило — вопросом со страницы «Правила расчёта». Людей нет (конституция, принцип I): ни номеров, ни
 * меток, ни списка дней — «чьи данные ушли» видит только администратор системы в журнале выборок. Раздел есть у ролей
 * строки «Выборки дней» (`SAMPLE_ROLES`).
 * - Свой запрос и свои состояния: подключения выше не ждут выборок. Отказ по правам (`403`) — фраза без «Повторить».
 * - Один интерфейс на 375/768/1280 px: сведения выборки (клиент, число дней) — в две колонки на любой ширине, условие
 *   переносится по словам.
 */

const SCREEN = 'MyAgents';

export function MySamples({ tz }: { tz: string }) {
  const headingId = useId();
  const { role } = useRole();
  const q = useQuery({ queryKey: mySamplesKey(role), queryFn: requestMySamples });

  useEffect(() => {
    if (q.error && shouldReport(q.error)) reportClientError(SCREEN, 'load samples', q.error);
  }, [q.error]);

  let body: ReactNode;
  if (q.isError) {
    body =
      q.error instanceof ApiError && q.error.status === 403 ? (
        <Card>
          <p className="flex items-center gap-2.5 px-6 py-4 text-[13.5px] text-ink-2 max-sm:px-4">
            <ShieldAlert size={16} strokeWidth={1.75} className="shrink-0 text-ink-3" aria-hidden />
            {T.samplesForbidden}
          </p>
        </Card>
      ) : (
        <LoadError
          title={TITLE.samplesError}
          error={q.error}
          busy={q.isFetching}
          onRetry={() => void q.refetch()}
        />
      );
  } else if (!q.data) body = <Loading />;
  else if (q.data.samples.length === 0)
    body = (
      <Card>
        <EmptyState icon={Inbox} title={TITLE.samplesEmpty} description={T.samplesEmpty} />
      </Card>
    );
  else body = <SampleList samples={q.data.samples} tz={tz} />;

  const count = q.data?.samples.length ?? 0;
  return (
    <section aria-labelledby={headingId}>
      <SectionHeading id={headingId} title={TITLE.samples} count={count} />
      <p className="mt-1 mb-3 text-[13px] text-ink-2">{T.samplesIntro}</p>
      {body}
    </section>
  );
}

function SampleList({ samples, tz }: { samples: readonly MySample[]; tz: string }) {
  const names = useConditionNames(samples);
  return (
    <Card>
      <ol aria-label={TITLE.samples} className="divide-y divide-line">
        {samples.map((s) => (
          <li key={s.id} className="px-6 py-4 max-sm:px-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[14px] font-semibold text-ink">{sampleSay.sample(s.id)}</span>
              <span className="text-[13px] text-ink-2 tabular-nums">{ruDateTime(s.createdAt, tz)}</span>
            </div>
            <p className="mt-1 text-[13.5px] break-words text-ink">
              <span className="sr-only">{LABEL.condition}: </span>
              {describeCondition(s, names)}
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-2 max-sm:gap-x-4">
              <Fact label={LABEL.client}>{s.client?.name ?? LABEL.fromUi}</Fact>
              <Fact label={LABEL.days}>
                <span className="tabular-nums">{sampleSay.days(s.days)}</span>
              </Fact>
            </dl>
          </li>
        ))}
      </ol>
    </Card>
  );
}

function Loading() {
  return (
    <div aria-busy="true" aria-label={LABEL.samplesLoading}>
      <Card className="divide-y divide-line">
        {[0, 1].map((i) => (
          <div key={i} className="space-y-2 px-6 py-4 max-sm:px-4">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-3/5" />
          </div>
        ))}
      </Card>
    </div>
  );
}
